import React, { useEffect, useState } from "react";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { BaseButton, BaseCheckbox, BaseInput } from "src/components";
import { SpaceTypeT } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import produce from "immer";
import moment from "moment";
import { Row } from "react-table";
import { editClosingRecurrenceAsync } from "src/api/product/product-api";
import CustomTimePicker from "src/components/CustomTimePicker";
import RangeDatepicker from "src/components/RangeDatepicker";
import { RESERVATION_POLICY_QUERY_KEY } from "src/hooks/product/useGetReservationPolicy";
import { dayOptions, UnionDayOfWeekType } from "src/pages/workOrder/workOrder-types";
import { useToast } from "src/recoil/toast/hook";
import { axiosInstance } from "src/utils";
import ClosedPeriodTable from "../components/ClosedPeriodTable";
import {
  CLOSING_TITLE_INVALID_MESSAGE,
  CLOSING_TITLE_PLACEHOLDER,
  ClosingRecurrenceRow,
  fromEffectiveDate,
  fromIsoDaysOfWeek,
  fromUnavailableMinutes,
  isClosingTitleInvalid,
  toClosingRecurrence,
} from "../utils/closingTime";
import { DAYS, toClosingTimePolicyOwner } from "./ClosedPeriod";
import { ValidationType } from "./ClosedPeriodRegister";

export interface ClosedPeriodListForm {
  startDate?: Date | null;
  endDate?: Date | null;
  days?: Array<"SUN" | "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT">;
  title?: string;
  // 휴무 항목 1건은 시간 한 쌍만 가진다. 테이블 셀 구조를 등록 모달과 맞추려고 배열로 둔다.
  times?: {
    startTime?: string;
    endTime?: string;
  }[];
}

const ClosedPeriodEditColumns = [
  {
    Header: "휴무 기간",
    accessor: "startDate",
    width: 240,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([row.original.startDate!, row.original.endDate!]);

      const handleOnDateRangeChange = (dateRange: [Date | null, Date | null]) => {
        const [startDate, endDate] = dateRange;
        setDateRange(dateRange);

        setClosedPeriodList(
          produce((draftArr) => {
            draftArr.forEach((draft, index) => {
              if (index === row.index) {
                draft.startDate = startDate;
                draft.endDate = endDate;
              }
            });
          }),
        );
      };
      return (
        <div className="minmax220">
          <RangeDatepicker minDate={new Date()} dateRange={dateRange} onChange={handleOnDateRangeChange} />
        </div>
      );
    },
  },
  {
    Header: "휴무 요일",
    accessor: "days",
    width: 320,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const days = row?.original?.days;
      return (
        <div className="flex-center-center h40">
          <BaseCheckbox
            label="전체"
            id={`allCheck-${row.index}`}
            name={`allCheck-${row.index}`}
            className="mr10"
            checked={days?.length === 7}
            onChange={(checked) => {
              if (checked) {
                // 모든 요일 넣기
                setClosedPeriodList(
                  produce((draftArr) => {
                    draftArr.forEach((draft, index) => {
                      if (index === row.index) {
                        draft.days = DAYS as any;
                      }
                    });
                  }),
                );
              } else {
                // 빈 배열 넣기
                setClosedPeriodList(
                  produce((draftArr) => {
                    draftArr.forEach((draft, index) => {
                      if (index === row.index) {
                        draft.days = [];
                      }
                    });
                  }),
                );
              }
            }}
          />
          {dayOptions.map((day) => (
            <div key={day.value}>
              <BaseCheckbox
                id={`${day.value}-${row.index}`}
                name={`${day.value}-${row.index}`}
                className="mr4 chip-case-product"
                label={day.label}
                checked={row?.original?.days?.some((item: UnionDayOfWeekType) => item === day.value) || false}
                onChange={(checked: boolean) => {
                  let weeks: UnionDayOfWeekType[] = row?.original?.days ? Array.from(row?.original?.days) : [];
                  if (checked) {
                    weeks.push(day.value);
                  } else {
                    weeks = weeks.filter((week) => week !== day.value);
                  }

                  setClosedPeriodList(
                    produce((draftArr) => {
                      draftArr.forEach((draft, index) => {
                        if (index === row.index) {
                          draft.days = weeks;
                        }
                      });
                    }),
                  );
                }}
              />
            </div>
          ))}
        </div>
      );
    },
  },
  {
    Header: "휴무 시간",
    accessor: "times",
    width: 320,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const times = row?.original?.times ?? [{ startTime: "", endTime: "" }];
      return times?.map((timeData, timeIndexX) => {
        return (
          <div key={timeIndexX} className="flex-center-center">
            <CustomTimePicker
              name="startTime"
              className="w120"
              timeIntervals={30}
              setDate={(date) => {
                // 휴무시간 > endTime 보다 startTime을 이후 시간으로 했을 때 endTime "" 처리
                const start = moment(date, "HH:mm").format("YYYY-MM-DD HH:mm");
                const end = moment(timeData.endTime, "HH:mm").format("YYYY-MM-DD HH:mm");
                const isStartTimeAfterEnd = moment(start).isAfter(end);

                setClosedPeriodList(
                  produce((draftArr) => {
                    draftArr.forEach((draft, index) => {
                      if (index === row.index) {
                        draft?.times?.forEach((time, timeIndexY) => {
                          if (timeIndexY === timeIndexX) {
                            time.startTime = date;
                            if (isStartTimeAfterEnd) time.endTime = "";
                          }
                        });
                      }
                    });
                  }),
                );
              }}
              selectedTime={timeData.startTime}
              midnight
              portalToBody
              placeholder=" "
            />
            <span className="mx10">-</span>
            <CustomTimePicker
              name="endTime"
              className="w120"
              timeIntervals={30}
              minTime={timeData.startTime}
              setDate={(date) => {
                setClosedPeriodList(
                  produce((draftArr) => {
                    draftArr.forEach((draft, index) => {
                      if (index === row.index) {
                        draft?.times?.forEach((time, timeIndexY) => {
                          if (timeIndexY === timeIndexX) {
                            time.endTime = date;
                          }
                        });
                      }
                    });
                  }),
                );
              }}
              selectedTime={timeData.endTime}
              midnight
              portalToBody
              placeholder=" "
            />
          </div>
        );
      });
    },
  },
  {
    Header: "휴무 사유",
    accessor: "title",
    width: 300,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const title = row?.original?.title ?? "";

      return (
        <BaseInput
          name={`title-${row.index}`}
          value={title}
          placeholder={CLOSING_TITLE_PLACEHOLDER}
          errorText={isClosingTitleInvalid(title) ? CLOSING_TITLE_INVALID_MESSAGE : undefined}
          onChange={(value: string) => {
            setClosedPeriodList(
              produce((draftArr) => {
                draftArr.forEach((draft, index) => {
                  if (index === row.index) {
                    draft.title = value;
                  }
                });
              }),
            );
          }}
        />
      );
    },
  },
];

interface Props {
  spaceType: SpaceTypeT;
  serviceType: UnionServiceType;
  serviceId: string;
  isEditable?: boolean;
  closingRecurrence: ClosingRecurrenceRow;
  space?: any;
  isDefault: boolean;
}

const ClosedPeriodEdit = ({ spaceType, serviceType, serviceId, closingRecurrence, space, isDefault }: Props) => {
  const { setBaseModal, setAbstractModalZ2 } = useModal();
  const queryClient = useQueryClient();
  const { openToast } = useToast();
  const [validations, setValidations] = useState<ValidationType[]>([]);

  const defaultValue: ClosedPeriodListForm = {
    startDate: fromEffectiveDate(closingRecurrence?.effectiveFrom),
    endDate: fromEffectiveDate(closingRecurrence?.effectiveTo),
    days: fromIsoDaysOfWeek(closingRecurrence?.daysOfWeek) as ClosedPeriodListForm["days"],
    title: closingRecurrence?.title ?? "",
    times: [
      {
        startTime: fromUnavailableMinutes(closingRecurrence?.unavailableFrom),
        endTime: fromUnavailableMinutes(closingRecurrence?.unavailableUntil),
      },
    ],
  };

  const [closedPeriodList, setClosedPeriodList] = useState<ClosedPeriodListForm[]>([defaultValue]);

  const owner = toClosingTimePolicyOwner(serviceType, serviceId);
  const target = isDefault ? { facilityType: spaceType } : { facilityId: space?.id };

  const { mutate, isPending } = useMutation({
    mutationFn: (closedPeriod: ClosedPeriodListForm) =>
      editClosingRecurrenceAsync(axiosInstance, {
        ...owner,
        ...target,
        id: closingRecurrence.id!,
        ...toClosingRecurrence({
          startDate: closedPeriod.startDate,
          endDate: closedPeriod.endDate,
          days: closedPeriod.days,
          title: closedPeriod.title,
          startTime: closedPeriod.times?.[0]?.startTime,
          endTime: closedPeriod.times?.[0]?.endTime,
        }),
      }),
  });

  useEffect(() => {
    if (validations.length > 0) {
      const dateRequired = closedPeriodList.some((item) => !item.startDate || !item.endDate);
      const daysRequired = closedPeriodList.some((item) => item.days?.length === 0);
      const timeRequired = closedPeriodList.some((item) => item.times?.some((time) => !time.startTime || !time.endTime));
      const titleInvalid = closedPeriodList.some((item) => isClosingTitleInvalid(item.title));

      !dateRequired && setValidations((prev) => prev.filter((item) => item.feild !== "date"));
      !daysRequired && setValidations((prev) => prev.filter((item) => item.feild !== "days"));
      !timeRequired && setValidations((prev) => prev.filter((item) => item.feild !== "time"));
      !titleInvalid && setValidations((prev) => prev.filter((item) => item.feild !== "title"));
    }
  }, [closedPeriodList]);

  const handleValidation = (): boolean => {
    let pass = true;

    closedPeriodList.forEach((item) => {
      if (!item.startDate || !item.endDate) {
        pass = false;
        setValidations((prev) => [...prev, { message: "휴무 기간을 선택해주세요.", feild: "date" }]);
      }

      if (item.days && item.days.length === 0) {
        pass = false;
        setValidations((prev) => [...prev, { message: "휴무 요일을 선택해주세요.", feild: "days" }]);
      }

      if (item.times?.some((time) => !time.startTime || !time.endTime)) {
        pass = false;
        setValidations((prev) => [...prev, { message: "휴무 시간을 선택해주세요.", feild: "time" }]);
      }

      if (isClosingTitleInvalid(item.title)) {
        pass = false;
        setValidations((prev) => [...prev, { message: CLOSING_TITLE_INVALID_MESSAGE, feild: "title" }]);
      }
    });

    if (pass) onEditClosedData();
    return false;
  };

  const onEditClosedData = () => {
    if (!closingRecurrence.id) {
      openToast({ content: "휴무일 정보를 찾을 수 없습니다.", type: "FAIL" });
      return;
    }

    // 개별 설정은 공용공간 ID 가 있어야 어느 정책에 넣을지 정해진다
    if (!isDefault && !space?.id) {
      openToast({ content: "공용공간 정보를 찾을 수 없습니다.", type: "FAIL" });
      return;
    }

    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      children: "저장 후에는 변경 사항이 즉시 적용됩니다.",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        mutate(closedPeriodList[0], {
          onSuccess: () => {
            openToast({ content: "저장 되었습니다.", isSystemToast: true });
            setAbstractModalZ2({ isOpen: false });
          },
          // 실패해도 확인 모달은 닫고 목록은 최신 상태로 되돌린다
          onSettled: () => {
            queryClient.refetchQueries({ queryKey: [RESERVATION_POLICY_QUERY_KEY] });
            setBaseModal({ isOpen: false });
          },
        });
      },
    });
  };

  return (
    <>
      <section className="base-abstract-modal__contents px30">
        <div className="base-abstract-modal__contents-subtitle">
          <span className="font-weight-bold" style={{ fontSize: 24 }}>
            휴무일 수정
          </span>
        </div>

        <section className="base-abstract-modal__contents-minmax-height">
          <ClosedPeriodTable
            setClosedPeriodList={setClosedPeriodList}
            columns={ClosedPeriodEditColumns}
            data={closedPeriodList ?? []}
          ></ClosedPeriodTable>
        </section>
      </section>

      <section className="base-abstract-modal__btn-wrap">
        {closedPeriodList.length > 0 && (
          <div className="flex-center">
            {/* 휴무 기간 메세지 */}
            {validations?.some((item) => item.feild === "date") && (
              <p className="validation-text">{validations?.find((item) => item.feild === "date")?.message}</p>
            )}
            {/* 휴무 요일 메세지 */}
            {!validations?.some((item) => item.feild === "date") && validations?.some((item) => item.feild === "days") && (
              <p className="validation-text">{validations?.find((item) => item.feild === "days")?.message}</p>
            )}
            {/* 휴무 시간 메세지 */}
            {!validations?.some((item) => item.feild === "date") &&
              !validations?.some((item) => item.feild === "days") &&
              validations?.some((item) => item.feild === "time") && (
                <p className="validation-text">{validations?.find((item) => item.feild === "time")?.message}</p>
              )}
          </div>
        )}

        <BaseButton title={"취소"} className="color-white" onClick={() => setAbstractModalZ2({ isOpen: false })} />
        <BaseButton
          title={"저장"}
          className=""
          disabled={isPending}
          onClick={() => {
            handleValidation();
          }}
        />
      </section>
    </>
  );
};

export default ClosedPeriodEdit;
